import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label
} from "reactstrap";

import { useAbsenceStaffHook } from "../../hooks/useAbsence";
import useGlobalState from "../../hooks/useGlobalState";
import { useSession } from "../../hooks/useSession";
import { onError } from "../../utils/format-error";
import { toast } from "react-toastify";

const INITIAL_COUNTDOWN = 10;
const TIMER_COLOR = "#75BC1E";

const getValidatedCountdown = value => {
  if (value === null || value === undefined) {
    return INITIAL_COUNTDOWN;
  }

  const parsedValue = Number(
    String(value)
      .trim()
      .replace(",", ".")
  );

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return INITIAL_COUNTDOWN;
  }

  return Math.round(parsedValue);
};

const formatCountdown = value => {
  const minutes = Math.floor(value / 60);
  const seconds = String(value % 60).padStart(2, "0");

  return `${minutes}:${seconds}`;
};

const AbsenceActivityModal = ({ signOut }) => {
  const {
    isAbsent,
    executionCode,
    absencesType,
    validateActivityCount
  } = useAbsenceStaffHook();
  const { confirmSessionActivity } = useSession();
  const sessionProfile = JSON.parse(sessionStorage.getItem("centerSelected"));
  const optionCount = useGlobalState(
    "option.cuidados_-_confirmación_de_actividad_(segudos)"
  );

  const initialCountdown = getValidatedCountdown(optionCount);
  const intervalRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [countdown, setCountdown] = useState(initialCountdown);
  const circleRadius = 60;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const countdownProgress = countdown / initialCountdown;
  const circleDrawableLength = circleCircumference;
  const remainingLength = circleDrawableLength * countdownProgress;
  const consumedLength = circleDrawableLength - remainingLength;
  const remainingOffset = -consumedLength;

  useEffect(() => {
    if (isAbsent) {
      if (validateActivityCount >= 1) {
        signOut({ preventDefault: () => {} });
        return;
      }
      setIsOpen(true);
      setCountdown(initialCountdown);
    }
  }, [isAbsent, initialCountdown, executionCode]);

  useEffect(() => {
    if (!isOpen) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    intervalRef.current = setInterval(() => {
      setCountdown(currentCountdown => {
        if (currentCountdown <= 1) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          signOut({ preventDefault: () => {} }, absencesType);
          return 0;
        }

        return currentCountdown - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen]);

  const confirmSession = async () => {
    try {
      await confirmSessionActivity({
        centerSelectedId: sessionProfile?.id || null
      });
      setIsOpen(false);
    } catch (error) {
      onError(error, toast);
    }
  };

  return (
    <Modal isOpen={isOpen} centered>
      <ModalHeader>Validación de Actividad</ModalHeader>
      <ModalBody className="text-center">
        <Label className="mb-2">
          <h4>Todavia estas ahi?</h4>
        </Label>
        <div
          style={{
            display: "flex",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: 150,
              height: 150,
              borderRadius: "50%",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "radial-gradient(circle at center, rgba(255,255,255,0.98) 0%, rgba(247,250,243,0.98) 55%, rgba(235,241,227,0.92) 100%), radial-gradient(circle at 20% 20%, rgba(255,255,255,0.9) 0, rgba(255,255,255,0.9) 18px, transparent 19px), radial-gradient(circle at 80% 22%, rgba(255,255,255,0.8) 0, rgba(255,255,255,0.8) 26px, transparent 27px), radial-gradient(circle at 18% 75%, rgba(255,255,255,0.8) 0, rgba(255,255,255,0.8) 20px, transparent 21px), radial-gradient(circle at 75% 78%, rgba(255,255,255,0.72) 0, rgba(255,255,255,0.72) 32px, transparent 33px)",
              overflow: "hidden"
            }}
          >
            <svg
              width="140"
              height="140"
              viewBox="0 0 140 140"
              style={{
                position: "absolute",
                inset: 5,
                transform: "rotate(-90deg)",
                transformOrigin: "center"
              }}
            >
              <circle
                cx="70"
                cy="70"
                r={circleRadius}
                fill="none"
                stroke={TIMER_COLOR}
                strokeWidth="9"
                strokeDasharray={`${remainingLength} ${circleCircumference}`}
                strokeDashoffset={remainingOffset}
                strokeLinecap="round"
              />
              <circle
                cx="70"
                cy="70"
                r={circleRadius}
                fill="none"
                stroke="rgba(117, 188, 30, 0.18)"
                strokeWidth="9"
                strokeDasharray={`${consumedLength} ${circleCircumference}`}
                strokeLinecap="round"
              />
            </svg>
            <div
              style={{
                color: TIMER_COLOR,
                fontSize: 36,
                lineHeight: 1,
                fontWeight: 700,
                letterSpacing: 0.5
              }}
            >
              {formatCountdown(countdown)}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="justify-content-start">
        <Button
          color="success"
          autoFocus
          onClick={() => confirmSession()}
          className="px-4"
        >
          SI
        </Button>
        <Button color="danger" onClick={signOut} className="px-4">
          No
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AbsenceActivityModal;
